'use client'

import { useEffect, useRef, useState } from 'react'

const cards = [
  {
    title: 'Showcase your expertise',
    description:
      'Build a profile that highlights what you actually do with AI - your prompts, workflows, and real results.',
    icon: (
      <svg aria-hidden="true" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    title: 'Discover what works',
    description:
      'Browse prompts and techniques from practitioners across industries. Learn from people solving problems like yours.',
    icon: (
      <svg aria-hidden="true" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),
  },
  {
    title: 'Connect with your people',
    description:
      'Find collaborators, mentors, and peers who share your AI interests. Build relationships that accelerate your growth.',
    icon: (
      <svg aria-hidden="true" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M9 21v-2a4 4 0 0 1 3-3.87" />
        <circle cx="12" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <circle cx="20" cy="7" r="4" />
      </svg>
    ),
  },
]

export default function ValueCards() {
  const sectionRef = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time init from browser API, no cascading render
    setReducedMotion(mq.matches)

    const el = sectionRef.current
    let observer: IntersectionObserver | null = null

    if (mq.matches || !el) {
      setVisible(true)
    } else {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true)
            observer?.disconnect()
          }
        },
        { threshold: 0.15 },
      )
      observer.observe(el)
    }

    const handler = (e: MediaQueryListEvent) => {
      setReducedMotion(e.matches)
      if (e.matches) {
        setVisible(true)
        observer?.disconnect()
      }
    }
    mq.addEventListener('change', handler)

    return () => {
      observer?.disconnect()
      mq.removeEventListener('change', handler)
    }
  }, [])

  return (
    <section
      ref={sectionRef}
      className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 md:pt-10 pb-20 sm:pb-24 md:pb-28"
    >
      <h2
        className="font-[family-name:var(--font-heading)] text-2xl md:text-3xl font-extrabold text-center text-[var(--color-text-primary)] mb-12 sm:mb-16"
        style={
          reducedMotion
            ? undefined
            : {
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
              }
        }
      >
        Built for people who make AI&nbsp;work.
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 md:gap-8">
        {cards.map((card, i) => (
          <div
            key={card.title}
            className="rounded-[var(--card-radius)] border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 sm:p-8 flex flex-col gap-4 shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow"
            style={
              reducedMotion
                ? undefined
                : {
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(24px)',
                    transition: `opacity 0.5s ease-out ${0.15 + i * 0.12}s, transform 0.5s ease-out ${0.15 + i * 0.12}s`,
                  }
            }
          >
            <div className="text-[var(--color-brand-indigo)]">{card.icon}</div>
            <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-[var(--color-text-primary)]">
              {card.title}
            </h3>
            <p className="text-sm sm:text-base leading-relaxed text-[var(--color-text-secondary)]">
              {card.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}
